import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface LegalSection {
  id: string;
  number: string;
  title: string;
  body: ReactNode;
  subsections?: Array<{
    id: string;
    number: string;
    title: string;
    body: ReactNode;
  }>;
}

export interface LegalLayoutProps {
  eyebrow: string;
  title: string;
  intro?: ReactNode;
  lastUpdated: string;
  sections: LegalSection[];
}

export function LegalLayout({
  eyebrow,
  title,
  intro,
  lastUpdated,
  sections,
}: LegalLayoutProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const allIds = sections.flatMap((s) => [s.id, ...(s.subsections?.map((sub) => sub.id) ?? [])]);
    const headings = allIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((h) => observerRef.current!.observe(h));
    return () => observerRef.current?.disconnect();
  }, [sections]);

  return (
    <div className="mx-auto w-full max-w-container-lg px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <header className="mx-auto max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-caps text-primary">
          {eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl text-ink leading-display tracking-display sm:text-5xl">
          {title}
        </h1>
        <p className="mt-3 text-sm text-ink-muted">Last updated {lastUpdated}</p>
        {intro ? (
          <div className="mt-6 text-lg leading-relaxed text-ink-secondary">{intro}</div>
        ) : null}
      </header>

      <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14">
        {/* TOC — sticky on desktop, collapsible details on mobile */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <details className="lg:open:!block group rounded-md border border-border-soft bg-surface p-4 lg:border-0 lg:bg-transparent lg:p-0" open>
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold uppercase tracking-caps text-ink-muted lg:cursor-default lg:pointer-events-none">
              <span>On this page</span>
              <svg
                aria-hidden
                viewBox="0 0 20 20"
                className="h-4 w-4 transition-transform group-open:rotate-180 lg:hidden"
                fill="currentColor"
              >
                <path d="M5.5 7.5L10 12l4.5-4.5" />
              </svg>
            </summary>
            <nav aria-label="Table of contents" className="mt-3 lg:mt-0">
              <ol className="space-y-1.5 text-sm">
                {sections.map((s) => {
                  const isActive = activeId === s.id ||
                    s.subsections?.some((sub) => sub.id === activeId);
                  return (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={cn(
                          'flex gap-2 rounded-sm px-2 py-1 transition-colors',
                          isActive
                            ? 'bg-primary-soft text-primary font-medium'
                            : 'text-ink-secondary hover:bg-surface-sunken hover:text-ink',
                        )}
                      >
                        <span className="tabular-nums text-ink-muted">{s.number}</span>
                        <span className="truncate">{s.title}</span>
                      </a>
                      {s.subsections?.length ? (
                        <ol className="mt-1 ml-4 space-y-1 border-l border-border-soft pl-2">
                          {s.subsections.map((sub) => (
                            <li key={sub.id}>
                              <a
                                href={`#${sub.id}`}
                                className={cn(
                                  'flex gap-2 rounded-sm px-2 py-1 text-xs transition-colors',
                                  activeId === sub.id
                                    ? 'text-primary font-medium'
                                    : 'text-ink-muted hover:text-ink',
                                )}
                              >
                                <span className="tabular-nums">{sub.number}</span>
                                <span className="truncate">{sub.title}</span>
                              </a>
                            </li>
                          ))}
                        </ol>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </details>
        </aside>

        {/* Content — capped to ~65ch reading width */}
        <article className="max-w-[65ch] space-y-12">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-2xl text-primary tabular-nums leading-display">
                  {s.number}
                </span>
                <h2 className="font-display text-2xl text-ink leading-display tracking-display sm:text-3xl">
                  {s.title}
                </h2>
              </div>
              <div className="mt-4 space-y-4 text-base leading-relaxed text-ink-secondary sm:text-lg">
                {s.body}
              </div>
              {s.subsections?.length ? (
                <div className="mt-8 space-y-8 border-l border-border-soft pl-6">
                  {s.subsections.map((sub) => (
                    <section key={sub.id} id={sub.id} className="scroll-mt-24">
                      <div className="flex items-baseline gap-3">
                        <span className="font-mono text-sm text-ink-muted tabular-nums">
                          {sub.number}
                        </span>
                        <h3 className="font-display text-xl text-ink leading-display">
                          {sub.title}
                        </h3>
                      </div>
                      <div className="mt-3 space-y-3 text-base leading-relaxed text-ink-secondary">
                        {sub.body}
                      </div>
                    </section>
                  ))}
                </div>
              ) : null}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
